import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Plugin } from '../plugin'
import { Operation } from '../types'

const SUBMIT_CANCELLATION_REASON = {
  noContext: 'No scenario context available',
  noEntity: 'No current record available',
  notSignedOff:
    'This room does not meet the requirements to be signed off. It may have unresolved warnings or already be signed off.',
} as const

/**
 * Calls the API to sign off the current space lite record's survey, ignoring
 * the submits of every other {@link Operation}
 */
export const executeSignOff = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.submit.request.observe().pipe(
      rxjs.filter(action => action.data.operation === Operation.SignOff),
      rxjs.withLatestFrom(
        plugin.collection.feature.selectors.context.asObservable,
        plugin.collection.plugins.currentEntityPlugin.feature.selectors.getId()
          .asObservable,
      ),
      rxjs.mergeMap(([action, context, spaceRecordId]) => {
        if (!ents.core.admin.Context.ForScenario.isForScenario(context)) {
          return rxjs.of(
            feature.actions.submit.cancellation.from(action).create({
              reason: SUBMIT_CANCELLATION_REASON.noContext,
              request: action.data,
            }),
          )
        }

        if (!ents.extra.space.Record.Id.isId(spaceRecordId)) {
          return rxjs.of(
            feature.actions.submit.cancellation.from(action).create({
              reason: SUBMIT_CANCELLATION_REASON.noEntity,
              request: action.data,
            }),
          )
        }

        return plugin.api.signOff({ context, spaceRecordId }).pipe(
          rxjs.map(affectedIds => {
            // The backend only returns the records it actually acted on, so
            // an absent id means the record didn't meet the requirements.
            if (!affectedIds.includes(spaceRecordId)) {
              return feature.actions.submit.cancellation.from(action).create({
                reason: SUBMIT_CANCELLATION_REASON.notSignedOff,
                request: action.data,
              })
            }

            return feature.actions.submit.success
              .from(action)
              .create({ request: action.data })
          }),
          rxjs.catchError((error: Error) =>
            rxjs.of(
              feature.actions.submit.error
                .from(action)
                .create({ error, request: action.data }),
            ),
          ),
        )
      }),
    ),
)
